import { useRef, useEffect, useState } from "react";
import "./PathfindingVisualizer.css";

const PathfindingVisualizer = () => {
  const canvasRef = useRef(null);
  const [gridSize] = useState({ rows: 25, cols: 40 });
  const [cellSize] = useState(20);
  const [grid, setGrid] = useState([]);
  const [startPoint, setStartPoint] = useState(null);
  const [endPoint, setEndPoint] = useState(null);
  const [mode, setMode] = useState("start"); // 'start', 'end', 'draw-obstacles', 'drag-obstacles'
  const [isDrawing, setIsDrawing] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [path, setPath] = useState([]);
  const [visitedNodes, setVisitedNodes] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [autoRecalculate, setAutoRecalculate] = useState(false);
  const [isDraggingObstacle, setIsDraggingObstacle] = useState(false);
  const [draggedObstacles, setDraggedObstacles] = useState([]);
  const [dragOffset, setDragOffset] = useState({ row: 0, col: 0 });

  // Initialize grid
  useEffect(() => {
    const newGrid = [];
    for (let row = 0; row < gridSize.rows; row++) {
      const currentRow = [];
      for (let col = 0; col < gridSize.cols; col++) {
        currentRow.push({
          row,
          col,
          type: "empty",
          gCost: Infinity,
          hCost: 0,
          fCost: Infinity,
          parent: null,
          visited: false,
        });
      }
      newGrid.push(currentRow);
    }
    setGrid(newGrid);
  }, [gridSize]);

  // Auto-recalculate path when obstacles change
  useEffect(() => {
    if (
      autoRecalculate &&
      startPoint &&
      endPoint &&
      !isSearching &&
      !isDraggingObstacle
    ) {
      const timer = setTimeout(() => {
        aStarSilent();
      }, 100);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    grid,
    autoRecalculate,
    startPoint,
    endPoint,
    isSearching,
    isDraggingObstacle,
  ]);

  // Draw grid whenever state changes
  useEffect(() => {
    if (grid.length > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      canvas.width = gridSize.cols * cellSize;
      canvas.height = gridSize.rows * cellSize;

      drawGrid(ctx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, gridSize, cellSize, path, visitedNodes, draggedObstacles]);

  useEffect(() => {
    return () => {
      if (window.dragPathUpdateTimeout) {
        clearTimeout(window.dragPathUpdateTimeout);
      }
    };
  }, []);

  const drawGrid = (ctx) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    for (let row = 0; row < gridSize.rows; row++) {
      for (let col = 0; col < gridSize.cols; col++) {
        const x = col * cellSize;
        const y = row * cellSize;
        const cell = grid[row][col];

        let fillColor = "#ffffff";

        // Check if this is where we're dragging obstacles
        const isDraggedObstacleHere = draggedObstacles.some(
          (obstacle) =>
            obstacle.currentRow === row && obstacle.currentCol === col
        );

        const isInPath = path.some(
          (pathCell) => pathCell.row === row && pathCell.col === col
        );

        const isVisited = visitedNodes.some(
          (visitedCell) => visitedCell.row === row && visitedCell.col === col
        );

        // Show dragged obstacles at new positions
        if (isDraggedObstacleHere && isDraggingObstacle) {
          fillColor = "#455A64"; // Lighter obstacle color when dragging
        } else if (cell.type === "start") {
          fillColor = "#4CAF50";
        } else if (cell.type === "end") {
          fillColor = "#F44336";
        } else if (cell.type === "obstacle") {
          fillColor = "#263238";
        } else if (isInPath) {
          fillColor = "#FFD700";
        } else if (isVisited) {
          fillColor = "#E3F2FD";
        } else {
          fillColor = "#ffffff";
        }

        ctx.fillStyle = fillColor;
        ctx.fillRect(x, y, cellSize, cellSize);

        // Special border for dragged obstacles
        if (isDraggedObstacleHere && isDraggingObstacle) {
          ctx.strokeStyle = "#FF9800";
          ctx.lineWidth = 3;
          ctx.strokeRect(x, y, cellSize, cellSize);
        } else {
          ctx.strokeStyle = cell.type === "obstacle" ? "#37474F" : "#e0e0e0";
          ctx.lineWidth = 1;
          ctx.strokeRect(x, y, cellSize, cellSize);
        }

        if (cell.type === "start") {
          ctx.fillStyle = "white";
          ctx.font = "bold 12px Arial";
          ctx.textAlign = "center";
          ctx.fillText("S", x + cellSize / 2, y + cellSize / 2 + 4);
        } else if (cell.type === "end") {
          ctx.fillStyle = "white";
          ctx.font = "bold 12px Arial";
          ctx.textAlign = "center";
          ctx.fillText("E", x + cellSize / 2, y + cellSize / 2 + 4);
        }
      }
    }
  };

  // A* Algorithm Implementation
  const heuristic = (nodeA, nodeB) => {
    return Math.abs(nodeA.row - nodeB.row) + Math.abs(nodeA.col - nodeB.col);
  };

  const getNeighbors = (node, grid) => {
    const neighbors = [];
    const directions = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ];

    for (const [dRow, dCol] of directions) {
      const newRow = node.row + dRow;
      const newCol = node.col + dCol;

      if (
        newRow >= 0 &&
        newRow < gridSize.rows &&
        newCol >= 0 &&
        newCol < gridSize.cols &&
        grid[newRow][newCol].type !== "obstacle"
      ) {
        neighbors.push(grid[newRow][newCol]);
      }
    }

    return neighbors;
  };

  const reconstructPath = (endNode) => {
    const path = [];
    let currentNode = endNode;

    while (currentNode) {
      path.unshift({
        row: currentNode.row,
        col: currentNode.col,
      });
      currentNode = currentNode.parent;
    }

    return path;
  };

  // Silent A* for real-time recalculation
  const aStarSilent = async () => {
    if (!startPoint || !endPoint) return;

    const calculationGrid = grid.map((row) =>
      row.map((cell) => ({
        ...cell,
        gCost: Infinity,
        hCost: 0,
        fCost: Infinity,
        parent: null,
        visited: false,
      }))
    );

    const startNode = calculationGrid[startPoint.row][startPoint.col];
    const endNode = calculationGrid[endPoint.row][endPoint.col];

    startNode.gCost = 0;
    startNode.hCost = heuristic(startNode, endNode);
    startNode.fCost = startNode.gCost + startNode.hCost;

    const openSet = [startNode];
    const closedSet = [];

    while (openSet.length > 0) {
      let currentNode = openSet[0];
      for (let i = 1; i < openSet.length; i++) {
        if (
          openSet[i].fCost < currentNode.fCost ||
          (openSet[i].fCost === currentNode.fCost &&
            openSet[i].hCost < currentNode.hCost)
        ) {
          currentNode = openSet[i];
        }
      }

      openSet.splice(openSet.indexOf(currentNode), 1);
      closedSet.push(currentNode);

      if (currentNode === endNode) {
        const finalPath = reconstructPath(endNode);
        setPath(finalPath);
        return;
      }

      const neighbors = getNeighbors(currentNode, calculationGrid);

      for (const neighbor of neighbors) {
        if (closedSet.includes(neighbor)) continue;

        const tentativeGCost = currentNode.gCost + 1;

        if (tentativeGCost < neighbor.gCost) {
          neighbor.parent = currentNode;
          neighbor.gCost = tentativeGCost;
          neighbor.hCost = heuristic(neighbor, endNode);
          neighbor.fCost = neighbor.gCost + neighbor.hCost;

          if (!openSet.includes(neighbor)) {
            openSet.push(neighbor);
          }
        }
      }
    }

    setPath([]);
  };

  // Original A* with visualization
  const aStar = async () => {
    if (!startPoint || !endPoint) {
      alert("Please place both start and end points!");
      return;
    }

    setIsSearching(true);
    setPath([]);
    setVisitedNodes([]);

    const newGrid = grid.map((row) =>
      row.map((cell) => ({
        ...cell,
        gCost: Infinity,
        hCost: 0,
        fCost: Infinity,
        parent: null,
        visited: false,
      }))
    );

    const startNode = newGrid[startPoint.row][startPoint.col];
    const endNode = newGrid[endPoint.row][endPoint.col];

    startNode.gCost = 0;
    startNode.hCost = heuristic(startNode, endNode);
    startNode.fCost = startNode.gCost + startNode.hCost;

    const openSet = [startNode];
    const closedSet = [];
    const visitedForVisualization = [];

    while (openSet.length > 0) {
      let currentNode = openSet[0];
      for (let i = 1; i < openSet.length; i++) {
        if (
          openSet[i].fCost < currentNode.fCost ||
          (openSet[i].fCost === currentNode.fCost &&
            openSet[i].hCost < currentNode.hCost)
        ) {
          currentNode = openSet[i];
        }
      }

      openSet.splice(openSet.indexOf(currentNode), 1);
      closedSet.push(currentNode);
      currentNode.visited = true;

      if (currentNode.type === "empty") {
        visitedForVisualization.push({
          row: currentNode.row,
          col: currentNode.col,
        });
      }

      if (currentNode === endNode) {
        const finalPath = reconstructPath(endNode);
        setPath(finalPath);
        setVisitedNodes(visitedForVisualization);
        setGrid([...newGrid]);
        setIsSearching(false);
        return;
      }

      const neighbors = getNeighbors(currentNode, newGrid);

      for (const neighbor of neighbors) {
        if (closedSet.includes(neighbor)) continue;

        const tentativeGCost = currentNode.gCost + 1;

        if (tentativeGCost < neighbor.gCost) {
          neighbor.parent = currentNode;
          neighbor.gCost = tentativeGCost;
          neighbor.hCost = heuristic(neighbor, endNode);
          neighbor.fCost = neighbor.gCost + neighbor.hCost;

          if (!openSet.includes(neighbor)) {
            openSet.push(neighbor);
          }
        }
      }

      if (visitedForVisualization.length % 10 === 0) {
        setVisitedNodes([...visitedForVisualization]);
        setGrid([...newGrid]);
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
    }

    setVisitedNodes(visitedForVisualization);
    setGrid([...newGrid]);
    setIsSearching(false);
    alert("No path found!");
  };

  const clearPath = () => {
    setPath([]);
    setVisitedNodes([]);
    const newGrid = grid.map((row) =>
      row.map((cell) => ({
        ...cell,
        gCost: Infinity,
        hCost: 0,
        fCost: Infinity,
        parent: null,
        visited: false,
      }))
    );
    setGrid(newGrid);
  };

  const getCellFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);

    return { row, col };
  };

  const handleCanvasClick = (event) => {
    if (isDraggingObstacle) return; // Don't handle clicks while dragging

    const { row, col } = getCellFromEvent(event);

    if (row >= 0 && row < gridSize.rows && col >= 0 && col < gridSize.cols) {
      const newGrid = [...grid];

      if (mode === "start") {
        if (
          newGrid[row][col].type === "obstacle" ||
          newGrid[row][col].type === "end"
        )
          return;

        if (startPoint) {
          newGrid[startPoint.row][startPoint.col].type = "empty";
        }

        newGrid[row][col].type = "start";
        setStartPoint({ row, col });
        setMode("end");
        if (!autoRecalculate) clearPath();
      } else if (mode === "end") {
        if (
          newGrid[row][col].type === "obstacle" ||
          newGrid[row][col].type === "start"
        )
          return;

        if (endPoint) {
          newGrid[endPoint.row][endPoint.col].type = "empty";
        }

        newGrid[row][col].type = "end";
        setEndPoint({ row, col });
        setMode("draw-obstacles");
        if (!autoRecalculate) clearPath();
      } else if (mode === "draw-obstacles") {
        if (
          newGrid[row][col].type !== "start" &&
          newGrid[row][col].type !== "end"
        ) {
          if (newGrid[row][col].type === "obstacle") {
            newGrid[row][col].type = "empty";
          } else {
            newGrid[row][col].type = "obstacle";
          }
          if (!autoRecalculate) clearPath();
        }
      }
      // No click action for drag-obstacles mode

      setGrid(newGrid);
    }
  };

  const getConnectedObstacles = (startRow, startCol, grid) => {
    const connected = [];
    const visited = new Set();
    const queue = [{ row: startRow, col: startCol }];

    // Directions for checking neighbors (4-directional)
    const directions = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ];

    while (queue.length > 0) {
      const { row, col } = queue.shift();
      const key = `${row}-${col}`;

      if (visited.has(key)) continue;
      if (row < 0 || row >= gridSize.rows || col < 0 || col >= gridSize.cols)
        continue;
      if (grid[row][col].type !== "obstacle") continue;

      visited.add(key);
      connected.push({ row, col });

      // Add neighbors to queue
      for (const [dRow, dCol] of directions) {
        const newRow = row + dRow;
        const newCol = col + dCol;
        const newKey = `${newRow}-${newCol}`;

        if (!visited.has(newKey)) {
          queue.push({ row: newRow, col: newCol });
        }
      }
    }

    return connected;
  };

  const aStarWithDraggedObstacles = async (draggedObstacles) => {
    if (!startPoint || !endPoint) return;

    // Create a temporary grid that includes the dragged obstacles at their current positions
    const tempGrid = grid.map((row) =>
      row.map((cell) => ({
        ...cell,
        gCost: Infinity,
        hCost: 0,
        fCost: Infinity,
        parent: null,
        visited: false,
      }))
    );

    // Add dragged obstacles to the temporary grid
    draggedObstacles.forEach((obstacle) => {
      if (
        obstacle.currentRow >= 0 &&
        obstacle.currentRow < gridSize.rows &&
        obstacle.currentCol >= 0 &&
        obstacle.currentCol < gridSize.cols
      ) {
        tempGrid[obstacle.currentRow][obstacle.currentCol].type = "obstacle";
      }
    });

    const startNode = tempGrid[startPoint.row][startPoint.col];
    const endNode = tempGrid[endPoint.row][endPoint.col];

    // Don't calculate if start or end is blocked by dragged obstacles
    if (startNode.type === "obstacle" || endNode.type === "obstacle") {
      setPath([]);
      return;
    }

    startNode.gCost = 0;
    startNode.hCost = heuristic(startNode, endNode);
    startNode.fCost = startNode.gCost + startNode.hCost;

    const openSet = [startNode];
    const closedSet = [];

    while (openSet.length > 0) {
      let currentNode = openSet[0];
      for (let i = 1; i < openSet.length; i++) {
        if (
          openSet[i].fCost < currentNode.fCost ||
          (openSet[i].fCost === currentNode.fCost &&
            openSet[i].hCost < currentNode.hCost)
        ) {
          currentNode = openSet[i];
        }
      }

      openSet.splice(openSet.indexOf(currentNode), 1);
      closedSet.push(currentNode);

      if (currentNode === endNode) {
        const finalPath = reconstructPath(endNode);
        setPath(finalPath);
        return;
      }

      const neighbors = getNeighbors(currentNode, tempGrid);

      for (const neighbor of neighbors) {
        if (closedSet.includes(neighbor)) continue;

        const tentativeGCost = currentNode.gCost + 1;

        if (tentativeGCost < neighbor.gCost) {
          neighbor.parent = currentNode;
          neighbor.gCost = tentativeGCost;
          neighbor.hCost = heuristic(neighbor, endNode);
          neighbor.fCost = neighbor.gCost + neighbor.hCost;

          if (!openSet.includes(neighbor)) {
            openSet.push(neighbor);
          }
        }
      }
    }

    // No path found
    setPath([]);
  };

  const handleMouseDown = (event) => {
    const { row, col } = getCellFromEvent(event);

    if (row >= 0 && row < gridSize.rows && col >= 0 && col < gridSize.cols) {
      const cell = grid[row][col];

      if (mode === "draw-obstacles") {
        // Drawing mode - can draw/erase obstacles
        if (cell.type !== "start" && cell.type !== "end") {
          setIsDrawing(true);
          setIsErasing(cell.type === "obstacle");

          const newGrid = [...grid];
          newGrid[row][col].type =
            cell.type === "obstacle" ? "empty" : "obstacle";
          setGrid(newGrid);
          if (!autoRecalculate) clearPath();
        }
      } else if (mode === "drag-obstacles") {
        // Dragging mode - can only drag existing obstacles
        if (cell.type === "obstacle") {
          console.log("Starting to drag obstacle group at:", row, col);

          // Get all connected obstacles
          const connectedObstacles = getConnectedObstacles(row, col, grid);
          console.log("Connected obstacles:", connectedObstacles.length);

          setIsDraggingObstacle(true);
          setDraggedObstacles(connectedObstacles);
          setDragOffset({ row: row, col: col }); // Store the click point as reference

          // Remove all connected obstacles from grid
          const newGrid = [...grid];
          connectedObstacles.forEach((obstacle) => {
            newGrid[obstacle.row][obstacle.col].type = "empty";
          });
          setGrid(newGrid);
        }
      }
    }
  };

  const handleMouseMove = (event) => {
    const { row, col } = getCellFromEvent(event);

    if (isDraggingObstacle && draggedObstacles.length > 0) {
      // Calculate the offset from the original click point
      const deltaRow = row - dragOffset.row;
      const deltaCol = col - dragOffset.col;

      // Update positions of all dragged obstacles
      const updatedObstacles = draggedObstacles.map((obstacle) => ({
        ...obstacle,
        currentRow: obstacle.row + deltaRow,
        currentCol: obstacle.col + deltaCol,
      }));

      setDraggedObstacles(updatedObstacles);

      // Real-time path recalculation during drag
      if (autoRecalculate && startPoint && endPoint) {
        // Use debouncing to avoid too frequent calculations
        clearTimeout(window.dragPathUpdateTimeout);
        window.dragPathUpdateTimeout = setTimeout(() => {
          aStarWithDraggedObstacles(updatedObstacles);
        }, 50); // 50ms delay for smooth performance
      }

      return;
    }

    if (isDrawing && mode === "draw-obstacles") {
      if (row >= 0 && row < gridSize.rows && col >= 0 && col < gridSize.cols) {
        const newGrid = [...grid];
        const cell = newGrid[row][col];

        if (cell.type === "start" || cell.type === "end") return;

        if (isErasing && cell.type === "obstacle") {
          newGrid[row][col].type = "empty";
          setGrid(newGrid);
          if (!autoRecalculate) clearPath();
        } else if (!isErasing && cell.type === "empty") {
          newGrid[row][col].type = "obstacle";
          setGrid(newGrid);
          if (!autoRecalculate) clearPath();
        }
      }
    }
  };

  // Update handleMouseUp to clean up the timeout
  const handleMouseUp = (event) => {
    // Clear any pending path update timeout
    if (window.dragPathUpdateTimeout) {
      clearTimeout(window.dragPathUpdateTimeout);
      window.dragPathUpdateTimeout = null;
    }

    if (isDraggingObstacle && draggedObstacles.length > 0) {
      const { row, col } = getCellFromEvent(event);
      const newGrid = [...grid];

      // Calculate the offset from the original click point
      const deltaRow = row - dragOffset.row;
      const deltaCol = col - dragOffset.col;

      // Check if all obstacles can be placed at their new positions
      let canPlaceAll = true;
      const newPositions = draggedObstacles.map((obstacle) => ({
        originalRow: obstacle.row,
        originalCol: obstacle.col,
        newRow: obstacle.row + deltaRow,
        newCol: obstacle.col + deltaCol,
      }));

      // Validate all new positions
      for (const pos of newPositions) {
        if (
          pos.newRow < 0 ||
          pos.newRow >= gridSize.rows ||
          pos.newCol < 0 ||
          pos.newCol >= gridSize.cols
        ) {
          canPlaceAll = false;
          break;
        }

        const targetCell = newGrid[pos.newRow][pos.newCol];
        if (targetCell.type === "start" || targetCell.type === "end") {
          canPlaceAll = false;
          break;
        }
      }

      if (canPlaceAll) {
        // Place all obstacles at new positions
        console.log("Placing obstacle group at new position");
        newPositions.forEach((pos) => {
          newGrid[pos.newRow][pos.newCol].type = "obstacle";
        });
      } else {
        // Return all obstacles to original positions
        console.log(
          "Invalid drop location, returning obstacle group to original positions"
        );
        draggedObstacles.forEach((obstacle) => {
          newGrid[obstacle.row][obstacle.col].type = "obstacle";
        });
      }

      setGrid(newGrid);
      setIsDraggingObstacle(false);
      setDraggedObstacles([]);
      setDragOffset({ row: 0, col: 0 });

      // Final path recalculation after drop (if auto-recalculate is on)
      if (autoRecalculate) {
        setTimeout(() => {
          aStarSilent();
        }, 100);
      }
    }

    setIsDrawing(false);
    setIsErasing(false);
  };

  const clearObstacles = () => {
    const newGrid = grid.map((row) =>
      row.map((cell) => ({
        ...cell,
        type: cell.type === "obstacle" ? "empty" : cell.type,
      }))
    );
    setGrid(newGrid);
  };

  const getCanvasCursor = () => {
    if (isDraggingObstacle) return "grabbing";
    if (mode === "drag-obstacles") return "grab";
    return "crosshair";
  };

  return (
    <div className="pathfinding-visualizer">
      <h1>A* Pathfinding Visualizer</h1>

      <div className="control-panel">
        <div className="mode-selector">
          <button
            className={mode === "start" ? "active" : ""}
            onClick={() => setMode("start")}
            disabled={isSearching}
          >
            📍 Place Start
          </button>
          <button
            className={mode === "end" ? "active" : ""}
            onClick={() => setMode("end")}
            disabled={isSearching}
          >
            🎯 Place End
          </button>
          <button
            className={mode === "draw-obstacles" ? "active" : ""}
            onClick={() => setMode("draw-obstacles")}
            disabled={isSearching}
          >
            🖊️ Draw Obstacles
          </button>
          <button
            className={mode === "drag-obstacles" ? "active" : ""}
            onClick={() => setMode("drag-obstacles")}
            disabled={isSearching}
          >
            👆 Drag Obstacles
          </button>
        </div>

        <div className="algorithm-controls">
          <button
            className="find-path-btn"
            onClick={aStar}
            disabled={isSearching || !startPoint || !endPoint}
          >
            {isSearching ? "🔍 Searching..." : "🚀 Find Path"}
          </button>
          <button
            className="clear-path-btn"
            onClick={clearPath}
            disabled={isSearching}
          >
            🧹 Clear Path
          </button>
        </div>

        <div className="real-time-controls">
          <label className="auto-recalc-toggle">
            <input
              type="checkbox"
              checked={autoRecalculate}
              onChange={(e) => setAutoRecalculate(e.target.checked)}
              disabled={isSearching}
            />
            <span>🔄 Real-time Path Updates</span>
          </label>
        </div>

        <div className="action-buttons">
          <button
            className="clear-btn"
            onClick={clearObstacles}
            disabled={isSearching}
          >
            🗑️ Clear Obstacles
          </button>
        </div>

        <div className="status">
          <span>
            Mode:{" "}
            <strong>
              {mode.charAt(0).toUpperCase() + mode.slice(1).replace("-", " ")}
            </strong>
          </span>
          {mode === "draw-obstacles" && (
            <span className="mode-hint">
              Click and drag to draw/erase obstacles
            </span>
          )}
          {mode === "drag-obstacles" && (
            <span className="mode-hint">
              Click and drag obstacles to move them around
            </span>
          )}
          {mode === "drag-obstacles" && autoRecalculate && (
            <span className="mode-hint">
              Real-time path updates while dragging obstacles
            </span>
          )}
          {path.length > 0 && (
            <span className="path-info">
              Path Length: <strong>{path.length - 1}</strong> steps
            </span>
          )}
          {autoRecalculate && (
            <span className="realtime-info">
              Real-time mode: Path updates automatically
            </span>
          )}
        </div>
      </div>

      <div className="canvas-container">
        <canvas
          ref={canvasRef}
          className="grid-canvas"
          style={{ cursor: getCanvasCursor() }}
          onClick={handleCanvasClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>
    </div>
  );
};

export default PathfindingVisualizer;
